/**
 * `#pricing` — a URL for the full-page wizard, without giving it a route.
 *
 * The wizard is opened from ~20 places through `billing-dialog-store`, so turning it into a
 * real route (`/app/s/subscription-plan/upgrade`) would mean teaching every one of those call
 * sites to navigate instead. A hash gets the two things that actually matter for free:
 *
 * - **Shareable link.** `…/app/s/subscription-plan#pricing` opens straight into it.
 * - **Working Back button.** Opening pushes a history entry, so Back closes the wizard
 *   instead of leaving the app — the real complaint about a full-page surface with no URL.
 *
 * It works on whatever page you are on: `/app/screenManagement#pricing` opens it over Screens.
 * The step is deliberately NOT in the hash — mid-flow state (chosen plan, screen count, a
 * Stripe element) is not resumable from a cold load, so a link to step 2 would lie.
 *
 * One exception: `#pricing?step=configure&term=yearly|monthly` (see `parseWizardHash`). The
 * Configure step for a live subscriber is derived from account data alone, so a link to it does
 * not lie. The params only ask — the wizard's own gates (`honourStartAtScreens`, `yearlyDisabled`)
 * still decide.
 *
 * Hash-opened means no context: no `minPlanRequired` floor, no `fromInactive`. That is correct
 * for a link someone PASTES — the plan-gated variants are opened by the code paths that know
 * which gate was hit.
 *
 * It was NOT correct for a refresh. Reloading a `#pricing` URL is the same cold load as far as
 * this hook is concerned, so a wizard opened from a banner (`wizardTitle: 'Select Your Plan'`,
 * a `minPlanRequired` floor, a recommended card) came back as the generic Subscription-Plan
 * variant — different title, and the "Downgrade to Free Plan" bar appearing, because that bar is
 * gated on `!minPlanRequired`. The user pressed F5 and got a different page than the one they
 * opened. So the variant-shaping half of the context is stashed for the lifetime of the tab's
 * visit to the wizard and handed back on a cold read; see `WIZARD_CONTEXT_KEYS`.
 */
import { useEffect, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router';
import { useAuthStore } from '@/stores/auth-store';
import { useBillingDialogStore } from '../stores/billing-dialog-store';

export const WIZARD_URL_HASH = '#pricing';

export interface WizardHashParams {
  /** `step=configure` — open on the screen-count step (live subscribers only). */
  step?: 'configure';
  /** `term=yearly|monthly` — the billing term to preselect. */
  term?: 'yearly' | 'monthly';
}

/** `#pricing` → `{}`; `#pricing?step=…&term=…` → params (unknown ones dropped); other anchors → `null`. */
export function parseWizardHash(hash: string): WizardHashParams | null {
  if (hash === WIZARD_URL_HASH) return {};
  if (!hash.startsWith(`${WIZARD_URL_HASH}?`)) return null;
  const params = new URLSearchParams(hash.slice(WIZARD_URL_HASH.length + 1));
  const out: WizardHashParams = {};
  if (params.get('step') === 'configure') out.step = 'configure';
  const term = params.get('term');
  if (term === 'yearly' || term === 'monthly') out.term = term;
  return out;
}

/** The dialog context a parsed link asks for; `purchaseSource` makes the sale attributable in PostHog. */
export function wizardHashParamsToContext(params: WizardHashParams): Record<string, unknown> {
  const context: Record<string, unknown> = {};
  if (params.step === 'configure') context.startAtScreensStep = true;
  if (params.term) context.initialBillingInterval = params.term;
  if (params.step || params.term) context.purchaseSource = 'pricing-link';
  return context;
}

const CONTEXT_KEY = 'scio3.wizardHashContext';

/**
 * The context a refresh is allowed to restore: everything that decides WHICH wizard you are
 * looking at, and nothing that DOES anything.
 *
 * Deliberately not the whole context object. `selectFree` opens the downgrade survey on mount,
 * `redirectChargeResult` puts a purchase receipt on screen (it has its own one-shot handoff in
 * post-subscribe-handoff), and `fromCancelFlow` / `cancelFlowPlanSwitch` attribute a survey row to
 * a flow the user is no longer in — replaying any of those on an F5 would be a side effect the
 * user never asked for a second time. `devStartAtStep` is a local canvas override and has no
 * business surviving anything.
 */
const WIZARD_CONTEXT_KEYS = [
  'wizardTitle',
  'wizardSubtitle',
  'planFeature',
  'minPlanRequired',
  'preSelectedPlan',
  'recommendedPlan',
  'purchaseSource',
  'subAccountId',
  'teamId',
  'skipPlanStep',
  'startAtScreensStep',
  'screensOnly',
  'fromInactive',
  'initialBillingInterval',
] as const;

/** The stash is keyed to the hash it was written under, so only a refresh of that URL restores it. */
const STASH_HASH_KEY = '__hash';

function writeWizardHashContext(context: Record<string, unknown>, forHash: string): void {
  const kept: Record<string, unknown> = {};
  for (const key of WIZARD_CONTEXT_KEYS) {
    if (context[key] !== undefined) kept[key] = context[key];
  }
  try {
    // Nothing worth keeping means nothing worth restoring — and leaving a previous entry in
    // place would let a plain open inherit the last gated one's variant.
    if (Object.keys(kept).length === 0) sessionStorage.removeItem(CONTEXT_KEY);
    else
      sessionStorage.setItem(CONTEXT_KEY, JSON.stringify({ ...kept, [STASH_HASH_KEY]: forHash }));
  } catch {
    // Private mode / quota. A refresh then falls back to the generic variant, which is the
    // behaviour this whole block improves on — never a reason to break opening the wizard.
  }
}

/**
 * NOT read-and-clear, unlike the post-subscribe receipt: a refresh must work the second and third
 * time too, and the entry is cleared when the wizard actually closes.
 */
function readWizardHashContext(forHash: string): Record<string, unknown> {
  try {
    const raw = sessionStorage.getItem(CONTEXT_KEY);
    if (!raw) return {};
    const parsed: unknown = JSON.parse(raw);
    if (!parsed || typeof parsed !== 'object') return {};
    const { [STASH_HASH_KEY]: writtenFor, ...context } = parsed as Record<string, unknown>;
    return writtenFor === forHash ? context : {};
  } catch {
    return {};
  }
}

function clearWizardHashContext(): void {
  try {
    sessionStorage.removeItem(CONTEXT_KEY);
  } catch {
    // Nothing to do: a stale entry can only be read while `#pricing` is on the URL, and closing
    // the wizard is what takes it off.
  }
}

/**
 * @param hasFinishedPurchase - true once a purchase has gone through (the wizard is showing its
 *   receipt). From then on `#pricing` comes off the URL, see the effect at the bottom.
 */
export function useWizardUrlHash(
  isOpen: boolean,
  onCloseRequested: () => void,
  hasFinishedPurchase = false,
): void {
  const location = useLocation();
  const navigate = useNavigate();
  const openDialog = useBillingDialogStore((s) => s.openDialog);
  const dialogContext = useBillingDialogStore((s) => s.dialogContext);

  // Read in an effect that deliberately does not depend on it (see below), so it comes from a ref
  // rather than a closure that would go stale between opens.
  const contextRef = useRef(dialogContext);
  contextRef.current = dialogContext;

  // Latest callbacks without re-running the sync effects when they change identity.
  const closeRef = useRef(onCloseRequested);
  closeRef.current = onCloseRequested;
  const openRef = useRef(openDialog);
  openRef.current = openDialog;

  const hashParams = parseWizardHash(location.hash);
  const hashWantsWizard = hashParams !== null;
  // Ref, like `dialogContext`: the cold-open effect keys on `hashWantsWizard`, not the params.
  const hashParamsRef = useRef(hashParams);
  hashParamsRef.current = hashParams;

  /**
   * Company info must be in the store before we auto-open on a cold load. The wizard picks its
   * starting step ONCE, from `useState`, using `isTrial` — which lives on company payment data
   * loaded async by GET_COMPANY_INFO. Open before that lands and `isTrial` reads false, so a
   * trial user starts on "Customize your plan" instead of the plan step, and nothing corrects
   * it afterwards. The bug only shows on a genuinely cold load (with the query cached it
   * resolves fast enough to look fine), which is exactly why it needs an explicit gate rather
   * than a timing assumption.
   */
  const isCompanyLoaded = !!useAuthStore((s) => s.company);

  // Always carry pathname + search. `navigate({ hash })` alone resolves as a partial path and
  // DROPS the query string — which would silently eat any param a page depends on the first
  // time the hash changed.
  const withHash = (hash: string) => ({
    pathname: location.pathname,
    search: location.search,
    hash,
  });

  /**
   * Whether `#pricing` has actually been on the URL during the CURRENT open.
   *
   * #2159 — "the wizard is open but the hash is gone" has two completely different causes, and
   * the close branch below could not tell them apart:
   *   - the user pressed Back, removing a hash that WAS there — a real request to close;
   *   - the wizard was just opened programmatically and the state→URL effect has not written the
   *     hash yet — the hash was never there, and nobody asked for anything.
   *
   * The second is the post-subscribe handoff: `BillingDialogs` restores the stashed receipt and
   * opens the wizard on a fresh document whose URL carries no hash. If `isCompanyLoaded` flips in
   * that window the effect below re-runs, reads the absent hash as a Back press, and closes the
   * wizard — whose close handler then nulls `chargeResult` and resets the step to NUMBER_SCREEN
   * on a 200ms timer. The hash lands immediately after, reopening the wizard from the URL with no
   * `redirectChargeResult`, and the timer lands it on "<plan> plan". The receipt
   * is gone (its handoff read is destructive), so the user is left on the configure-and-pay step
   * having just paid.
   *
   * Tracking it in a ref rather than adding `isOpen` to the deps keeps the two effects from
   * fighting over the round trip, which is the reason `isOpen` is excluded in the first place.
   * Assigned during render, like `contextRef` above, so it is already correct when the effects
   * run in the same commit.
   */
  const hashEstablishedRef = useRef(false);
  if (!isOpen) hashEstablishedRef.current = false;
  // After a purchase the hash is taken off on purpose (effect below), so its absence is not Back.
  else if (hasFinishedPurchase) hashEstablishedRef.current = false;
  else if (hashWantsWizard) hashEstablishedRef.current = true;

  // URL → state. Covers both a cold load on the link and the Back button removing the hash.
  useEffect(() => {
    if (hashWantsWizard && !isOpen) {
      if (!isCompanyLoaded) return; // retries when isCompanyLoaded flips
      // Restores the variant on a refresh; `{}` on a pasted link, which is the generic wizard.
      openRef.current('subscriptionWizard', {
        ...readWizardHashContext(location.hash),
        ...wizardHashParamsToContext(hashParamsRef.current ?? {}),
      });
    } else if (!hashWantsWizard && isOpen && hashEstablishedRef.current) {
      // Only a hash that WAS there and is now gone means Back — see the ref's note.
      closeRef.current();
    }
    // Deliberately excludes `isOpen`: including it would make this fire on the state→URL
    // round trip and fight the effect below.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hashWantsWizard, isCompanyLoaded]);

  // Only true once the wizard has actually been open in this session. Without it, the
  // clear-the-hash branch below fires on the FIRST render of a `#pricing` cold load — where
  // `isOpen` is still false because the effect above has only just requested the open — and
  // strips the hash before the store update lands, so the deep link silently does nothing.
  const hasBeenOpenRef = useRef(false);

  // State → URL. Push when opening (so Back can close it); replace when closing (so Back
  // doesn't bounce the user straight back into the wizard they just left).
  useEffect(() => {
    if (isOpen) {
      hasBeenOpenRef.current = true;
      // Stashed whenever the wizard is open, not only when the hash is being written: a cold load
      // restores the context and must leave it in place for the NEXT refresh.
      writeWizardHashContext(contextRef.current, hashWantsWizard ? location.hash : WIZARD_URL_HASH);
      // A parameterised `#pricing?…` is left as the link wrote it, so a refresh can read it back.
      if (!hashWantsWizard) {
        navigate(withHash(WIZARD_URL_HASH), { replace: false });
      }
    } else if (hasBeenOpenRef.current && hashWantsWizard) {
      // Closed for real, so the variant goes with it. Without this, opening the wizard later from
      // a plain entry point would inherit the last banner's title and plan floor.
      clearWizardHashContext();
      navigate(withHash(''), { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  /**
   * #2635 — once the purchase is done, the URL stops asking for the wizard.
   *
   * The receipt is still on screen, but `#pricing` on the URL means "open the plan picker" to any
   * fresh load. A phone that reloads the tab (after switching to a bank app to approve the charge,
   * or just to reclaim memory) reopened the wizard on the plan step, showing the plan the customer
   * had just paid for. Replacing the entry (not pushing) keeps Back from re-adding it.
   */
  useEffect(() => {
    if (!isOpen || !hasFinishedPurchase || !hashWantsWizard) return;
    clearWizardHashContext();
    navigate(withHash(''), { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, hasFinishedPurchase, hashWantsWizard]);
}
